import { isStaleCompletionResponse, type ChatEntryWithReactId } from "@/src/features/ai-chat/chatResponseReconciliation";
import type { CompletionUndoFinalizationResponse } from "./completionUndoFinalization";

/** Called only with a receipt-verified response and a currently captured principal. */
export function reconcileCompletionConversation<T extends ChatEntryWithReactId & { userId?: unknown; companyId?: unknown; question?: unknown }>(
  history: readonly T[], response: CompletionUndoFinalizationResponse,
) {
  const { extraFields, ...doc } = response.doc;
  const saved = { ...extraFields, ...doc };
  const matches = history.flatMap((entry, index) => entry.reactId === saved.reactId ? [index] : []);
  if (matches.length !== 1) return { chatHistory: [...history], applied: false, conversation: saved };
  const index = matches[0], previous = history[index];
  if (previous.userId !== undefined && previous.userId !== response.ownerId
    || previous.companyId !== undefined && previous.companyId !== response.companyId) throw new Error("Completion conversation owner changed");
  if (isStaleCompletionResponse(previous, saved)) return { chatHistory: [...history], applied: false, conversation: previous };
  const conversation = { ...previous, ...saved,
    ...(typeof previous.question === "string" && previous.question ? { question: previous.question } : {}) };
  const chatHistory = [...history]; chatHistory[index] = conversation;
  return { chatHistory, applied: true, conversation };
}

/** Restore only the original receipt-verified document, without evicting live rows. */
export function restoreCompletionConversation<T extends ChatEntryWithReactId & { userId?: unknown; companyId?: unknown; question?: unknown }>(
  history: readonly T[], response: CompletionUndoFinalizationResponse,
) {
  const result = reconcileCompletionConversation(history, response);
  const matches = history.filter(entry => entry.reactId === response.requestReactId);
  if (matches.length > 1 || matches.length === 1 && !result.applied) return result;
  const saved = result.conversation;
  const record = saved as Record<string, unknown>;
  const utterances = [record, ...(Array.isArray(record.workflowCompletions) ? record.workflowCompletions : [])];
  const restoredAudioCompletionKeys = utterances.flatMap(entry => entry && typeof entry === "object"
    && typeof entry.audioCompletionKey === "string" ? [entry.audioCompletionKey] : []);
  const conversation = { ...saved, restoredAudioCompletionKeys,
    restoredAudioAnswer: { text: record.audioAnswer, updatedDate: record.updatedDate, completionRevision: record.completionRevision } };
  const chatHistory = matches.length === 0
    ? [conversation as unknown as T, ...history]
    : result.chatHistory.map(entry => entry.reactId === response.requestReactId ? conversation as unknown as T : entry);
  return { chatHistory, applied: true, conversation };
}
